/**
 * Global Error Boundary Component
 *
 * Uses react-error-boundary library to catch component crashes
 * and display a user-friendly error UI with recovery options.
 */

import { AlertCircle, Home, RefreshCw } from "lucide-react";
import type { FallbackProps } from "react-error-boundary";
import { Button } from "@/components/ui/button";

/**
 * Global error fallback component
 *
 * Displayed when a component crashes. Shows:
 * - User-friendly error message
 * - Technical details (in development only)
 * - "Try Again" and "Go Home" recovery buttons
 */
export function GlobalErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const isDev = import.meta.env.DEV;

  // Safely extract error message
  let errorMessage = "Unknown error";
  let errorStack: string | undefined = undefined;

  if (error instanceof Error) {
    errorMessage = error.message;
    errorStack = error.stack;
  } else if (typeof error === "string") {
    errorMessage = error;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full space-y-6">
        {/* Error Icon */}
        <div className="flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-destructive" />
          </div>
        </div>

        {/* Error Message */}
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
          <p className="text-muted-foreground">
            An unexpected error occurred. This could be due to a network issue or a problem with the application.
          </p>
        </div>

        {/* Technical Details (Development Only) */}
        {isDev && (
          <details className="bg-muted p-4 rounded-md text-sm">
            <summary className="cursor-pointer font-medium mb-2 text-foreground">Technical Details</summary>
            <pre className="whitespace-pre-wrap break-words text-xs text-muted-foreground">{errorMessage}</pre>
            {errorStack && <pre className="whitespace-pre-wrap break-words text-xs text-muted-foreground mt-2">{errorStack}</pre>}
          </details>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={resetErrorBoundary} className="flex-1 gap-2">
            <RefreshCw className="w-4 h-4" />
            Try Again
          </Button>
          <Button variant="outline" onClick={() => (window.location.href = "/")} className="flex-1 gap-2">
            <Home className="w-4 h-4" />
            Go Home
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Export the fallback component for use with react-error-boundary
 */
export default GlobalErrorFallback;
